// Prediction Metrics API Client
// v3.7.0 Phase 1A: Confidence-based metrics (accuracy + effect size)

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

/**
 * Prediction Metrics Response Types (matching JSON Schema)
 */
export interface PredictionMetricsResponse {
  success: boolean;
  data: PredictionMetricsData;
  timestamp: string;
}

export interface PredictionMetricsData {
  version: string;
  window: string;
  horizon: '1D' | '3D' | '7D';
  sample_size: number;
  accuracy: AccuracyMetrics;
  effect_size: EffectSizeMetrics | null;
  consistency: number | null;  // Phase 1B: 0-1 range, null if insufficient data
  consistency_notes: string[];  // Phase 1B: Explanatory notes
  consistency_diagnostics?: ConsistencyDiagnostics;  // Phase 1B: Debug mode only
  notes?: string[];
  updated_at: string;
}

export interface ConsistencyDiagnostics {
  bucket_count: number;
  included_buckets: number;
  excluded_buckets: number;
  total_samples: number;
  min_bucket_size: number;
  max_bucket_size: number;
  bucket_date_ranges?: string[];
}

export interface AccuracyMetrics {
  value: number;          // 0-1 (e.g., 0.67 = 67%)
  ci_lower: number;       // Lower bound of 95% CI
  ci_upper: number;       // Upper bound of 95% CI
  method: 'wilson';
}

export interface EffectSizeMetrics {
  mean_abs_when_correct: number;     // Average absolute return when correct
  mean_signed_all: number | null;    // Mean signed return (all signals)
  winsorized: boolean;
  winsor_p?: number;                  // Winsorization percentile (e.g., 0.02)
}

/**
 * Query parameters for prediction metrics endpoint
 */
export interface PredictionMetricsQuery {
  signal: 'STRONG_BUY' | 'OPPORTUNITY' | 'BULL_TRAP' | 'STRONG_SELL';
  window?: string;     // e.g., '30d', '90d', 'all'
  horizon?: '1D' | '3D' | '7D';
}

/**
 * Fetch prediction quality metrics for a signal type.
 *
 * @param query - Query parameters
 * @returns Prediction metrics response
 * @throws Error if request fails
 */
export async function getPredictionMetrics(
  query: PredictionMetricsQuery
): Promise<PredictionMetricsResponse> {
  const params = new URLSearchParams({
    signal: query.signal,
    ...(query.window && { window: query.window }),
    ...(query.horizon && { horizon: query.horizon }),
  });

  const url = `${API_BASE}/api/metrics/prediction?${params}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch prediction metrics: ${response.status} ${response.statusText}`);
  }

  const data: PredictionMetricsResponse = await response.json();

  if (!data.success) {
    throw new Error('API returned error response');
  }

  return data;
}

/**
 * Format accuracy with confidence interval for display.
 *
 * @param accuracy - Accuracy metrics object
 * @returns Formatted string (e.g., "67% [35–88]")
 */
export function formatAccuracyWithCI(accuracy: AccuracyMetrics): string {
  const value = Math.round(accuracy.value * 100);
  const lower = Math.round(accuracy.ci_lower * 100);
  const upper = Math.round(accuracy.ci_upper * 100);

  return `${value}% [${lower}–${upper}]`;
}

/**
 * Get half-width of confidence interval (for compact display).
 *
 * @param accuracy - Accuracy metrics object
 * @returns Half-width as percentage (e.g., 21 for ±21%)
 */
export function getAccuracyHalfWidth(accuracy: AccuracyMetrics): number {
  return Math.round(((accuracy.ci_upper - accuracy.ci_lower) / 2) * 100);
}

/**
 * Format effect size for display.
 *
 * @param effectSize - Effect size metrics object
 * @returns Formatted string (e.g., "+6.7% avg")
 */
export function formatEffectSize(effectSize: EffectSizeMetrics): string {
  const value = (effectSize.mean_abs_when_correct * 100).toFixed(1);
  return `+${value}% avg`;
}

/**
 * Get sample size category for UI rendering.
 *
 * @param sampleSize - Number of signals
 * @returns 'insufficient' | 'small' | 'reliable'
 */
export function getSampleSizeCategory(sampleSize: number): 'insufficient' | 'small' | 'reliable' {
  if (sampleSize < 5) return 'insufficient';
  if (sampleSize < 20) return 'small';
  return 'reliable';
}

/**
 * Get sample size warning message.
 *
 * @param sampleSize - Number of signals
 * @returns Warning message or null
 */
export function getSampleSizeWarning(sampleSize: number): string | null {
  const category = getSampleSizeCategory(sampleSize);

  if (category === 'insufficient') {
    return `Insufficient data (${sampleSize} signals). Need at least 5 mature signals.`;
  }

  if (category === 'small') {
    return `Small sample (${sampleSize} signals). Metrics will stabilize with more data.`;
  }

  return null;
}

/**
 * Format consistency for display (Phase 1B).
 *
 * @param consistency - Consistency value (0-1) or null
 * @returns Formatted string (e.g., "73%") or "—"
 */
export function formatConsistency(consistency: number | null): string {
  if (consistency === null) return '—';
  return `${Math.round(consistency * 100)}%`;
}

/**
 * Get consistency label based on value (Phase 1B).
 *
 * @param consistency - Consistency value (0-1) or null
 * @returns Label: 'Volatile' | 'Moderate' | 'Stable' | 'Very Stable' | null
 */
export function getConsistencyLabel(consistency: number | null): string | null {
  if (consistency === null) return null;
  if (consistency >= 0.86) return 'Very Stable';
  if (consistency >= 0.61) return 'Stable';
  if (consistency >= 0.31) return 'Moderate';
  return 'Volatile';
}

/**
 * Get consistency chip color (Phase 1B).
 *
 * @param consistency - Consistency value (0-1) or null
 * @returns MUI chip color
 */
export function getConsistencyColor(consistency: number | null): 'success' | 'warning' | 'error' | 'default' {
  if (consistency === null) return 'default';
  if (consistency >= 0.61) return 'success';   // Stable/Very Stable
  if (consistency >= 0.31) return 'warning';   // Moderate
  return 'error';                               // Volatile
}
